// ------------------------------------------------------------------
// Content-stress matrix — story 2-137 (P10 §2, content grammar).
// ------------------------------------------------------------------
//
// THE CANONICAL FIXTURE for the content rules this story establishes: long
// titles, long descriptions, addresses, unbroken identifiers, URLs
// (never rendered raw — always offered through `CopyField`) and
// localization expansion (a translated UI string routinely runs 30-200%
// longer than its English source — German compound nouns are the
// standard stress case). `/app/design`'s "Content-stress matrix" Section
// imports this module and renders every case through real DS primitives
// (`PageHeader`, `RailPanel`, `Table`, `CopyField`) rather than hand-writing
// long strings inline in the gallery page.
//
// STORY 2-134 ADOPTS THIS AS THE SHARED e2e FIXTURE (named in this story's
// own done-when) — kept framework-free (no React/JSX import, plain data)
// so it is trivially importable from a Playwright spec as well as this
// gallery page, with nothing to transpile beyond bare TypeScript.
//
// What is deliberately NOT here: a "missing value" case. Missing-value
// rendering is a distinct concern (see `components/ui/MissingValue.tsx`)
// from stress-testing a PRESENT value's length — `MISSING_VALUE_FIELDS`
// below covers it as its own small fixture instead of overloading
// `ContentStressCase` with empty-string sentinels that would fight every
// consumer's own "is this required" typing.

export interface ContentStressCase {
  /** Stable identity — e2e keys assertions on this, not on the copy. */
  key: string;
  /** What this case stresses, for the demo's own case-selector label. */
  stresses: string;
  /** A long title — no natural line-break opportunity relied upon; must wrap
   * (not clip) inside a `min-w-0` flex/grid child. */
  title: string;
  /** A long supporting sentence — prose, wraps normally. */
  description: string;
  /** A long real-shaped postal address — commas, no single very-long token,
   * but long as a WHOLE line inside a narrow rail. */
  address: string;
  /** A long, whitespace-free token (uuid/hash/slug) — the case that breaks
   * layouts which only handle prose wrapping. Needs `break-all` (or
   * equivalent `overflow-wrap: anywhere`) wherever it renders inline. */
  identifier: string;
  /** Never rendered as raw text — always offered through `CopyField`,
   * which owns its own truncation/copy affordance. */
  url: string;
}

export const CONTENT_STRESS_CASES: ContentStressCase[] = [
  {
    key: 'long-content',
    stresses: 'Long title, description, address, identifier, URL (English)',
    title:
      'Quarterly Fire, Life-Safety and Emergency Egress Compliance Inspection — Building B, Level 4, Tenancy 4.12',
    description:
      'A routine inspection covering fire door integrity, exit signage illumination, emergency lighting battery duration, extinguisher service tags and the egress path clearance for the whole tenancy floor plate, cross-referenced against the prior quarter’s corrective actions.',
    address: 'Tenancy 4.12, Building B, 883 Plenty Road, South Morang, Victoria 3752, Australia',
    identifier: 'insp_9f2b7a1c4e6d8053a1f9c2b7e4d6805391a2c4e6d8053f1a2b7c9e4d6805391a',
    url: 'https://app.defectsnap.example/share/9f2b7a1c4e6d8053a1f9c2b7e4d6805391a2c4e6d8053f1a2b7c9e4d6805391a?ref=report-detail&expires=2026-09-30',
  },
  {
    key: 'localization-expansion',
    stresses: 'Localization expansion (German — 30-200% longer than English)',
    title: 'Regelmäßige Brandschutz- und Fluchtwegkontrolle für das gesamte Bürogebäude',
    description:
      'Rechtsschutzversicherungsgesellschaftenkontrollpflichtigenverzeichnis — die Wartungsprotokolle für Brandschutztüren, Notbeleuchtung, Fluchtwegkennzeichnung und Feuerlöscherprüfsiegel werden vollständig dokumentiert, archiviert und quartalsweise gegengeprüft.',
    address: 'Musterstraße 12a, Hinterhaus, 4. Obergeschoss, 10115 Berlin-Mitte, Deutschland',
    identifier: 'kontrollpflichtigenverzeichnis-2026-q3-gebaeude-b-vierte-etage-tenancy',
    url: 'https://app.defectsnap.example/de/freigabe/langlebiges-freigabetoken-fuer-die-uebersetzungserweiterung-2026',
  },
];

export interface MissingValueField {
  key: string;
  /** The field name, folded into `MissingValue`'s accessible name. */
  label: string;
}

// A representative row of fields that are routinely absent on a real
// record — paired with `components/ui/MissingValue.tsx` in the gallery
// demo and, later, in `DataList` (story 2-138).
export const MISSING_VALUE_FIELDS: MissingValueField[] = [
  { key: 'dueDate', label: 'Due date' },
  { key: 'assignee', label: 'Assigned to' },
  { key: 'notes', label: 'Notes' },
];
